<template>
  <div class="wanted-skeleton">
    <div class="wanted-skeleton-title">
      <div class="skeleton-line lg"></div>
    </div>

    <div class="wanted-skeleton-section">
      <div class="section-header">
        <div class="skeleton-rect section-icon"></div>
        <div class="skeleton-line md"></div>
      </div>
      <div class="section-progress">
        <div class="skeleton-line progress-track">
          <div
            v-for="milestoneIndex in milestoneCount"
            :key="`milestone-${milestoneIndex}`"
            class="skeleton-pill milestone"
          ></div>
        </div>
      </div>
      <div class="section-cards">
        <div v-for="cardIndex in cardCount" :key="`card-${cardIndex}`" class="section-card">
          <div class="skeleton-rect card-image"></div>
        </div>
      </div>
    </div>

    <div class="wanted-skeleton-bottom">
      <div class="skeleton-line lg"></div>
      <div class="skeleton-pill bottom-btn"></div>
    </div>

    <div
      v-for="activityIndex in activityCount"
      :key="`activity-${activityIndex}`"
      class="wanted-skeleton-activity"
    >
      <div class="activity-header">
        <div class="activity-header-left">
          <div class="skeleton-rect activity-icon"></div>
          <div class="skeleton-pill activity-tag"></div>
        </div>
        <div class="skeleton-circle activity-help"></div>
      </div>
      <div class="activity-content">
        <div
          v-for="taskIndex in taskCount"
          :key="`activity-${activityIndex}-task-${taskIndex}`"
          class="activity-task"
        >
          <div class="task-title">
            <div class="task-title-left">
              <div class="skeleton-line md"></div>
              <div class="skeleton-pill task-tag"></div>
            </div>
            <div class="task-title-right">
              <div class="skeleton-line time"></div>
              <div class="skeleton-pill task-btn"></div>
            </div>
          </div>
          <div class="task-images">
            <div
              v-for="productIndex in productCount"
              :key="`activity-${activityIndex}-task-${taskIndex}-product-${productIndex}`"
              class="task-image"
            >
              <div class="skeleton-rect image"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="wanted-skeleton-disclaimer">
      <div class="skeleton-line full"></div>
      <div class="skeleton-line full"></div>
      <div class="skeleton-line sm"></div>
    </div>

    <div class="wanted-skeleton-fixed-btn skeleton-pill"></div>
  </div>
</template>

<script setup lang="ts">
  defineOptions({
    name: 'WantedSkeleton'
  })

  const milestoneCount = 3
  const cardCount = 3
  const activityCount = 2
  const taskCount = 2
  const productCount = 4
</script>

<style scoped lang="stylus">
  @keyframes skeleton-shimmer
    0%
      left -150%
    100%
      left 150%

  skeleton-base()
    position relative
    // overflow hidden
    background #F5F6FA
    &:before
      content ''
      position absolute
      top 0
      left -150%
      width 150%
      height 100%
      background linear-gradient(90deg, rgba(245, 246, 250, 0) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(245, 246, 250, 0) 100%)
      animation skeleton-shimmer 1.6s ease-in-out infinite
      border-radius inherit

  .skeleton-line
    skeleton-base()
    width 100%
    height 12px
    border-radius 8px

    &.lg
      height 18px
      width 72%

    &.md
      width 60%

    &.sm
      height 10px
      width 40%

    &.full
      width 100%

  .skeleton-pill
    skeleton-base()
    width 100%
    height 24px
    border-radius 24px

  .skeleton-rect
    skeleton-base()
    width 100%
    height 48px
    border-radius 10px

  .skeleton-circle
    skeleton-base()
    width 20px
    height 20px
    border-radius 50%

  .wanted-skeleton
    position relative
    width 100%
    height calc(100% - 64px - 16px)
    padding 8px 16px
    background #fff
    box-sizing border-box
    overflow-y auto
    overflow-x hidden
    border-top 1px solid #f0f0f0

    &::-webkit-scrollbar
      width 4px

    &::-webkit-scrollbar-thumb
      background rgba(0, 0, 0, 0.2)
      border-radius 5px

    &::-webkit-scrollbar-track
      background transparent

    .wanted-skeleton-title
      display flex
      flex-direction column
      gap 8px

    .wanted-skeleton-section
      display flex
      flex-direction column
      gap 12px
      margin-top 12px
      padding 12px 8px
      border 1px solid #EEE
      border-radius 8px
      background #F5F6FA

      .section-header
        display flex
        align-items center
        justify-content space-between
        gap 8px

        .section-icon
          width 156px
          height 24px

        .skeleton-line
          flex 1

      .section-progress
        display flex
        flex-direction column
        gap 8px

        .progress-track
            display flex
            align-items center
            justify-content space-between
            gap 8px
            height 12px
            border-radius 21px
          .milestone
            height 30px
            max-width 48px
            border-radius 30px
            border 5px solid rgba(255,255,255,1)

      .section-cards
        display flex
        align-items stretch
        justify-content space-between
        gap 6px

        .section-card
          flex 1
          display flex
          flex-direction column
          align-items center
          gap 8px

          .card-image
            width 100%
            height 96px
            border-radius 12px

          .card-btn
            width 88px
            height 24px

    .wanted-skeleton-bottom
      margin 20px auto 12px auto
      display flex
      align-items center
      justify-content space-between
      gap 12px

      .skeleton-line
        flex 1

      .bottom-btn
        width 88px
        height 24px

    .wanted-skeleton-activity
      margin-top 12px
      padding 12px 8px
      border 1px solid #EEE
      border-radius 8px
      display flex
      flex-direction column
      gap 16px

      .activity-header
        display flex
        align-items center
        justify-content space-between
        gap 8px

        .activity-header-left
          display flex
          align-items center
          justify-content flex-start
          gap 8px

          .activity-icon
            width 52px
            height 24px

          .activity-tag
            width 72px
            height 16px
            border-radius 12px

        .activity-help
          width 20px
          height 20px

      .activity-content
        display flex
        flex-direction column
        gap 20px

        .activity-task
          display flex
          flex-direction column
          gap 12px

          .task-title
            display flex
            align-items center
            justify-content space-between
            gap 12px

            .task-title-left
              display flex
              flex-direction column
              align-items flex-start
              gap 8px

              .task-tag
                width 64px
                height 16px

            .task-title-right
              display flex
              flex-direction column
              align-items flex-end
              justify-content center
              gap 8px

              .time
                width 62px
                height 12px

              .task-btn
                width 72px
                height 28px

          .task-images
            display flex
            gap 8px
            overflow hidden

            .task-image
              width 92px
              height 100px

              .image
                width 100%
                height 100%
                border-radius 8px

    .wanted-skeleton-disclaimer
      margin 16px 0 82px 0
      display flex
      flex-direction column
      gap 8px

      .skeleton-line.full
        height 12px

      .skeleton-line.sm
        width 30%

    .wanted-skeleton-fixed-btn
      position fixed
      bottom 24px
      left 318px
      width 150px
      height 44px
      border-radius 22px

      &:before
        border-radius 22px
</style>
